// Shared constants for the agent-discovery surface of the site:
// robots.txt Content Signals, /llms.txt, /.well-known/* documents and the
// WebMCP tools all read from here so the story agents see stays consistent.

export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL || "https://key-stone.app";

export const KEYSTONE_APPS = [
  {
    slug: "loyalty_referrals",
    name: "Loyalty & Referrals",
    summary:
      "Points, VIP tiers, rewards and referral programs that turn one-time Shopify buyers into repeat customers.",
    page: "/loyalty",
    appStoreUrl: "https://apps.shopify.com/keystone-loyalty-rewards",
  },
  {
    slug: "product_options",
    name: "Product Options",
    summary:
      "Unlimited product options, variants and customizations for Shopify products — text fields, swatches, file uploads, conditional logic.",
    page: "/product-options",
    appStoreUrl: "https://apps.shopify.com/keystone-product-options-variants",
  },
  {
    slug: "ai_seo",
    name: "AI SEO",
    summary:
      "AI-powered SEO optimization for Shopify stores: meta tags, alt text, structured data and content improvements on autopilot.",
    page: "/ai-seo",
    appStoreUrl: "https://apps.shopify.com/key-ai-seo-1",
  },
];

// Markdown overview of the site. Served at /llms.txt and returned for
// `GET /` when an agent asks for `Accept: text/markdown` (see middleware).
export const SITE_MARKDOWN = `# KEYSTONE — Premium Shopify Apps for Growth & Loyalty

> KEYSTONE (https://key-stone.app) builds a suite of premium Shopify apps that
> help merchants boost customer loyalty, enhance product options, optimize SEO
> with AI, and accelerate growth.

## Apps

${KEYSTONE_APPS.map(
  (app) =>
    `- **${app.name}** (\`${app.slug}\`): ${app.summary}\n  Product page: ${SITE_URL}${app.page} · Shopify App Store: ${app.appStoreUrl}`,
).join("\n")}

## Key pages

- [Home](${SITE_URL}/): product overview and feature highlights
- [Loyalty & Rewards](${SITE_URL}/loyalty) · [Product Options & Variants](${SITE_URL}/product-options) · [AI SEO](${SITE_URL}/ai-seo)
- [Blog](${SITE_URL}/blogs): articles on Shopify growth, loyalty and SEO (RSS: ${SITE_URL}/feed.xml)
- [All articles](${SITE_URL}/blogs/archive): full index of every post, grouped by topic
- [Documentation](${SITE_URL}/docs/): setup and reference for all three apps — [Loyalty](${SITE_URL}/docs/loyalties/getting-started/introduction.html) · [Product Options](${SITE_URL}/docs/product-options/introduction/overview.html) · [AI SEO](${SITE_URL}/docs/aiSEO/getting-started.html)
- [Free tools](${SITE_URL}/tools): [Loyalty ROI calculator](${SITE_URL}/tools/loyalty-roi-calculator) · [Points value calculator](${SITE_URL}/tools/points-value-calculator) · [Referral program planner](${SITE_URL}/tools/referral-program-planner)
- [Changelog](${SITE_URL}/resources/changelog): release notes for all apps
- [Security](${SITE_URL}/security) · [Privacy](${SITE_URL}/privacy-policy) · [Terms](${SITE_URL}/terms-of-use)

## Machine-readable endpoints

- \`GET ${SITE_URL}/api/changelog?app=<slug>&locale=<code>&limit=<n>\` — public JSON changelog
  (app: ${KEYSTONE_APPS.map((a) => a.slug).join(" | ")}; type: fix | new | improvement)
- \`${SITE_URL}/api/mcp\` — remote MCP server (Streamable HTTP, bearer token required)
- \`GET ${SITE_URL}/api/agent/premium/changelog-digest\` — paid changelog digest (x402/MPP; sandbox)
- \`POST ${SITE_URL}/api/agent/register\` — agent self-registration → read-only access token
- \`POST ${SITE_URL}/api/oauth/token\` — OAuth client_credentials token endpoint
- \`${SITE_URL}/auth.md\` — how agents authenticate (OAuth client_credentials + ck_ bearer tokens)
- \`${SITE_URL}/openapi.json\` — OpenAPI 3.1 (incl. MPP x-payment-info)
- \`${SITE_URL}/.well-known/oauth-authorization-server\` — OAuth AS metadata (RFC 8414 + agent_auth)
- \`${SITE_URL}/.well-known/openid-configuration\` — OpenID Connect discovery
- \`${SITE_URL}/.well-known/ucp\` — Universal Commerce Protocol profile
- \`${SITE_URL}/.well-known/acp.json\` — Agentic Commerce Protocol discovery
- \`${SITE_URL}/.well-known/api-catalog\` — API catalog (RFC 9727)
- \`${SITE_URL}/.well-known/mcp/server-card.json\` — MCP server card
- \`${SITE_URL}/.well-known/agent-skills/index.json\` — agent skills index
- \`${SITE_URL}/sitemap.xml\` — sitemap
- \`${SITE_URL}/llms-full.txt\` — this overview plus the full text of all blog articles

## Contact

Questions about partnerships, support or MCP access tokens: use the contact
form on ${SITE_URL}/.
`;

// Agent Skill (SKILL.md) teaching agents how to consume the Keystone
// changelog API and remote MCP server. Served from
// /.well-known/agent-skills/keystone-changelog/SKILL.md and digested
// (sha256) in /.well-known/agent-skills/index.json — keep them in sync by
// always serving this exact string.
export const CHANGELOG_SKILL_NAME = "keystone-changelog";

export const CHANGELOG_SKILL_DESCRIPTION =
  "Read Keystone (key-stone.app) product changelogs via the public JSON API, or manage changelog entries, blog posts and resources through the remote MCP server with a bearer token.";

export const CHANGELOG_SKILL_MD = `---
name: ${CHANGELOG_SKILL_NAME}
description: ${CHANGELOG_SKILL_DESCRIPTION}
---

# Keystone Changelog

KEYSTONE (https://key-stone.app) ships premium Shopify apps:
${KEYSTONE_APPS.map((app) => `- ${app.name} (\`${app.slug}\`)`).join("\n")}

## Read the changelog (no auth required)

\`\`\`
GET ${SITE_URL}/api/changelog?app=loyalty_referrals&locale=en&limit=20
\`\`\`

Query parameters (all optional):

- \`app\`: ${KEYSTONE_APPS.map((a) => a.slug).join(" | ")}
- \`type\`: fix | new | improvement
- \`locale\`: locale code (e.g. en, de, fr). When set, titles/bullets come back resolved for that locale.
- \`limit\`: max entries (default 100)

The response is \`{ success: true, data: [...] }\` with entries sorted by release date descending.

## Manage entries over MCP (bearer token required)

A remote MCP server (Streamable HTTP) lives at \`${SITE_URL}/api/mcp\`:

\`\`\`
claude mcp add --transport http keystone-changelog ${SITE_URL}/api/mcp \\
  --header "Authorization: Bearer ck_YOUR_TOKEN"
\`\`\`

Tools:

- Changelog: \`list_changelog\`, \`create_changelog\`, \`update_changelog\`, \`delete_changelog\`
- Blog: \`list_blogs\`, \`get_blog\`, \`create_blog\`, \`update_blog\`, \`delete_blog\`
- Resources: \`list_resources\`, \`get_resource\`, \`create_resource\`, \`update_resource\`, \`delete_resource\`

Tokens (\`ck_...\`) are issued by the Keystone team; without one the server returns 401.
Full auth details: ${SITE_URL}/auth.md

## Other resources

- Site overview for agents: ${SITE_URL}/llms.txt
- Blog: ${SITE_URL}/blogs (RSS: ${SITE_URL}/feed.xml)
- API catalog: ${SITE_URL}/.well-known/api-catalog
`;
